import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { useLoadingBarState } from '../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { IMetricFundDataModel, IMetricsResponseDataModel } from '../../../data-models/metrics.data-model';
import { queryClient } from '../../../services/MaggieQueryClient';
import { updateCompanyFund } from '../../../services/queries/MaggieCompanyQueries';
import { getErrorMessage } from '../../../services/queryHelpers';
import { peekCompanyMetrics, setCompanyMetricsAtomJ } from '../../../services/state/CompanyMetricsStateJ';
import { LoadingId } from '../../../types';
import { TemporalPlainDateFromJsDate } from '../../../util/date-utilts';
import { partialEqualsIgnoreUnsetValues } from '../../../util/partialEquals';
import { MetricsQueryKey } from '../../PortfolioOverview/state/MetricsStateJ';
import { defaultViewDate } from '../../PortfolioOverview/state/PageStateJ';
import { selectedCompanyIdProfileAtom } from '../state/UIStateJ';

export function useUpdateInvestmentData() {
  const selectedCompanyId = useAtomValue(selectedCompanyIdProfileAtom);

  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  const refetchMetrics = useCallback(async () => {
    try {
      const defaultDate = TemporalPlainDateFromJsDate(defaultViewDate());
      await queryClient.invalidateQueries({ queryKey: [MetricsQueryKey, defaultDate] });
    } catch (e) {
      console.error(e);
    }
  }, []);

  const updateCompanyFundData = useAtomCallback(
    useCallback(
      async (_get, set, args: { companyFundId: number; payload: Partial<IMetricFundDataModel> }) => {
        const { companyFundId, payload } = args;
        try {
          const currentData = peekCompanyMetrics({ companyId: selectedCompanyId });
          const currentMetrics = currentData?.metrics.at(0);
          const currentFundData =
            currentMetrics?.fundData.find((fundData) => fundData.id === companyFundId) ??
            currentMetrics?.familyData?.find((fundData) => fundData.id === companyFundId);

          if (currentData && partialEqualsIgnoreUnsetValues(payload, currentFundData)) {
            return;
          }

          actions.startLoading(LoadingId.updateCompanyFund);
          const response = await updateCompanyFund({ companyFundId, payload });

          set(setCompanyMetricsAtomJ, {
            params: { companyId: selectedCompanyId },
            value: (current) => {
              if (!current) {
                return current ?? null;
              }
              const { metrics } = current;
              const patch = (rows: IMetricFundDataModel[] | undefined) =>
                rows?.map((row) => (row.id !== response.id ? row : { ...row, ...payload }));

              return {
                ...current,
                metrics: [
                  {
                    ...metrics[0],
                    fundData: patch(metrics[0].fundData) ?? [],
                    familyData: patch(metrics[0].familyData),
                  },
                ],
              } as IMetricsResponseDataModel;
            },
          });
          pushSuccessToast({ message: 'Investment data successfully updated' });
          refetchMetrics();
        } catch (e) {
          console.error(e);
          const message = getErrorMessage(e, 'Error updating investment data');
          pushErrorToast({ message });
        } finally {
          actions.stopLoading(LoadingId.updateCompanyFund);
        }
      },
      [actions, pushErrorToast, pushSuccessToast, refetchMetrics, selectedCompanyId]
    )
  );

  return useCallback(
    (companyFundId: number, payload: Partial<IMetricFundDataModel>) =>
      updateCompanyFundData({ companyFundId, payload }),
    [updateCompanyFundData]
  );
}
